import React from 'react';
import PolicyPageLayout from './PolicyPageLayout';

const Security = () => {
  const sections = [
    {
      title: "Our Security Commitment",
      content: [
        "At SoundFlowAI, security is not just a feature—it's foundational to everything we build. We employ industry-leading security practices to ensure your audio data and personal information remain protected at all times.",
        "Our security team continuously monitors for threats, conducts regular audits, and updates our defenses to stay ahead of emerging risks."
      ]
    },
    {
      title: "Data Encryption",
      content: [
        "All data transmitted to and from our platform is protected using TLS 1.3 encryption. Audio files stored on our servers are encrypted at rest using AES-256 encryption, one of the strongest encryption standards available."
      ],
      bullets: [
        "TLS 1.3 for all data in transit",
        "AES-256 encryption for data at rest",
        "Hardware Security Modules (HSMs) for key management",
        "Regular key rotation and secure key storage",
        "End-to-end encryption available for Enterprise plans"
      ]
    },
    {
      title: "Infrastructure Security",
      content: [
        "Our infrastructure is hosted on enterprise-grade cloud platforms with multiple layers of physical and network security. We utilize distributed data centers with redundant systems to ensure high availability and disaster recovery."
      ],
      bullets: [
        "SOC 2 Type II certified data centers",
        "24/7 physical security with biometric access controls",
        "Network firewalls and intrusion detection systems",
        "DDoS protection and traffic monitoring",
        "Regular penetration testing by third-party security firms"
      ]
    },
    {
      title: "Access Controls",
      content: [
        "We implement strict access controls to ensure only authorized personnel can access sensitive systems and data. All access is logged and regularly audited."
      ],
      bullets: [
        "Role-based access control (RBAC) for all systems",
        "Multi-factor authentication required for all employees",
        "Principle of least privilege enforcement",
        "Regular access reviews and deprovisioning",
        "Comprehensive audit logging and monitoring"
      ]
    },
    {
      title: "Application Security",
      content: [
        "Our development team follows secure coding practices and conducts regular security reviews. We maintain a responsible disclosure program and actively engage with the security research community."
      ],
      bullets: [
        "Secure Software Development Lifecycle (SSDLC)",
        "Static and dynamic application security testing",
        "Dependency scanning and vulnerability management",
        "Regular code reviews by security engineers",
        "Bug bounty program for responsible disclosure"
      ]
    },
    {
      title: "Incident Response",
      content: [
        "We maintain comprehensive incident response procedures to quickly detect, contain, and remediate security events. Our security team is available 24/7 to respond to potential threats."
      ],
      bullets: [
        "24/7 Security Operations Center (SOC)",
        "Automated threat detection and alerting",
        "Documented incident response procedures",
        "Regular tabletop exercises and drills",
        "Transparent communication during incidents"
      ]
    },
    {
      title: "Compliance & Certifications",
      content: [
        "We maintain compliance with major security and privacy frameworks to demonstrate our commitment to protecting your data."
      ],
      bullets: [
        "SOC 2 Type II certified",
        "ISO 27001 certified",
        "GDPR compliant",
        "CCPA compliant",
        "Annual third-party security audits"
      ]
    }
  ];

  const faqs = [
    {
      question: "How do you protect my audio files?",
      answer: "Your audio files are encrypted using AES-256 encryption both during upload (in transit) and while stored on our servers (at rest). Access to files is strictly controlled and logged."
    },
    {
      question: "Do you have a bug bounty program?",
      answer: "Yes, we maintain an active bug bounty program through HackerOne. Security researchers who responsibly disclose vulnerabilities are rewarded based on severity and impact."
    },
    {
      question: "How often do you conduct security audits?",
      answer: "We conduct continuous automated security testing and annual third-party penetration tests. Our SOC 2 and ISO 27001 certifications require annual audits by independent firms."
    },
    {
      question: "What happens if there's a data breach?",
      answer: "In the event of a confirmed data breach, we will notify affected users within 72 hours, provide details about the incident, and take immediate steps to secure systems and prevent future occurrences."
    },
    {
      question: "Can I enable additional security features?",
      answer: "Enterprise plans include additional security features such as SSO/SAML integration, custom security policies, dedicated infrastructure, and IP whitelisting."
    }
  ];

  return (
    <PolicyPageLayout
      title="Security"
      lastUpdated="February 28, 2026"
      sections={sections}
      faqs={faqs}
    />
  );
};

export default Security;
